import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getPlanFeatures, getAllPlans } from '../utils/planUtils';
import {
  ShieldCheckIcon,
  ChartBarIcon,
  EyeIcon,
  LockClosedIcon,
  ExclamationTriangleIcon,
  BoltIcon,
  GlobeAltIcon,
  CogIcon,
  StarIcon,
  CheckIcon,
  MagnifyingGlassIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';
import api from '../services/api';
import SEOHead from '../components/SEOHead';
import Navbar from '../components/Navbar';
import ProductStream from '../components/ProductStream';
import QuickScan from '../components/QuickScan';

export default function Landing() {
  const { user } = useAuth();

  const plans = getAllPlans().filter(plan => plan.key !== 'enterprise');

  const handleUpgrade = async (planType) => {
    try {
      if (planType === 'pro') {
        const response = await api.post('/payments/create-checkout-session', { plan: 'pro' });
        if (response.data.url) {
          window.location.href = response.data.url;
        }
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      alert('Failed to initiate checkout. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-950">
      <SEOHead
        fullTitle="TypoGuard: Automated Typosquatting & Phishing Detection"
        description="Protect your brand with TypoGuard's automated typosquatting detection. Monitor 1000+ TLDs for phishing attempts, vowels swaps, and homoglyphs. Start your free scan today."
        schema={{
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "TypoGuard",
          "applicationCategory": "SecurityApplication",
          "operatingSystem": "Web Browser",
          "featureList": [
            "TLD Swap Detection",
            "Phonetic & Vowel Swap Detection",
            "Dictionary Affix Analysis",
            "Wildcard DNS Shield",
            "Heuristic Risk Scoring"
          ],
          "offers": [
            {
              "@type": "Offer",
              "price": "0.00",
              "priceCurrency": "CHF",
              "name": "Free Tier"
            },
            {
              "@type": "Offer",
              "price": "5.00",
              "priceCurrency": "CHF",
              "name": "Pro Tier"
            }
          ]
        }}
      />
      {/* Navigation */}
      <Navbar />

      {/* Hero Section */}
      <div className="relative px-6 lg:px-8">
        <div className="mx-auto max-w-4xl pt-20 pb-12 sm:pt-48 sm:pb-16">
          <div className="text-center">

            {/* Logo & Mascot Decorative Container */}
            <div className="flex items-center justify-center mb-6 relative">
              <img
                src="/TGlogo.png"
                alt=""
                className="w-16 h-16 sm:w-20 sm:h-20 object-contain mr-4"
                aria-hidden="true"
              />
              {/* TGChibi Mascot */}
              <div className="relative">
                <img
                  src="/TGChibi.png"
                  alt=""
                  className="w-28 h-28 sm:w-32 sm:h-32 object-contain ml-2"
                  aria-hidden="true"
                />
                {/* Speech bubble */}
                <div className="absolute -top-8 -right-2 bg-gray-800 text-xs px-2 py-1 rounded-lg shadow-lg border border-gray-600 text-gray-300 opacity-0 hover:opacity-100 transition-opacity duration-300">
                  I'm here to help! 🛡️
                </div>
              </div>
            </div>

            <h1 className="text-6xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-8xl mb-6">
              <span style={{ color: '#c1f0c6', textShadow: '0 0 20px rgba(128, 128, 128, 0.4), 0 0 40px rgba(128, 128, 128, 0.3), 0 0 60px rgba(128, 128, 128, 0.2)' }}>
                TypoGuard
              </span>
            </h1>

            <p className="mt-6 text-2xl leading-8 text-gray-600 dark:text-gray-300 font-medium">
              Your silent guardian against <span className="text-primary-600 dark:text-primary-400">typosquatting</span> and <span className="text-primary-600 dark:text-primary-400">phishing</span> attacks.
            </p>
            <div className="mt-8 flex items-center justify-center gap-x-6">
              <Link
                to="/login"
                className="rounded-xl bg-gray-900 dark:bg-white px-8 py-4 text-lg font-semibold text-white dark:text-gray-900 shadow-lg hover:bg-gray-800 dark:hover:bg-gray-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-900 dark:focus-visible:outline-white transition-all duration-200 transform hover:-translate-y-1 hover:shadow-xl"
              >
                Get started
              </Link>
              <Link to="/#pricing" className="text-lg font-semibold leading-6 text-gray-900 dark:text-gray-100 hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
                Pricing <span aria-hidden="true">→</span>
              </Link>
            </div>

            {/* Quick Scan Teaser Component */}
            <QuickScan />
          </div>
        </div>
      </div>

      {/* User Plan Section - Only show if user is logged in */}
      {user && (
        <div className="py-16 sm:py-20 bg-gradient-to-br from-mint-50 to-blue-50 dark:from-gray-800 dark:to-gray-900">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <div className="mx-auto max-w-4xl">
              <div className="text-center mb-12">
                <div className="flex items-center justify-center mb-4">
                  <StarIcon className="h-8 w-8 text-mint-500 mr-3" />
                  <h2 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
                    Your Current Plan
                  </h2>
                </div>
                <div className={`inline-flex flex-col items-center px-6 py-3 rounded-full text-lg font-semibold ${getPlanFeatures(user.plan_type).bgColor} ${getPlanFeatures(user.plan_type).color} dark:bg-opacity-20 shadow-glow border border-gray-200 dark:border-dark-600`}>
                  <span>{getPlanFeatures(user.plan_type).name}</span>
                  {getPlanFeatures(user.plan_type).price && (
                    <span className="text-sm font-normal opacity-80 mt-1">{getPlanFeatures(user.plan_type).price}</span>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Plan Features */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 flex items-center">
                    <CheckIcon className="h-6 w-6 text-green-500 mr-3" />
                    What's Included
                  </h3>
                  <ul className="space-y-4">
                    {getPlanFeatures(user.plan_type).features.map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <CheckIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700 dark:text-gray-300">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Plan Limitations */}
                {getPlanFeatures(user.plan_type).limitations.length > 0 && (
                  <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8">
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 flex items-center">
                      <ExclamationTriangleIcon className="h-6 w-6 text-yellow-500 mr-3" />
                      Plan Limitations
                    </h3>
                    <ul className="space-y-4">
                      {getPlanFeatures(user.plan_type).limitations.map((limitation, index) => (
                        <li key={index} className="flex items-start">
                          <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500 mr-3 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700 dark:text-gray-300">{limitation}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Upgrade CTA for free plan users */}
                {user.plan_type === 'free' && (
                  <div className="lg:col-span-2">
                    <div className="bg-gradient-to-r from-mint-500 to-blue-500 rounded-2xl shadow-lg p-8 text-center text-white">
                      <h3 className="text-2xl font-bold mb-4">Ready to Upgrade?</h3>
                      <p className="text-lg mb-6 opacity-90">
                        Unlock unlimited scans and advanced protection with Pro or get custom enterprise features
                      </p>
                      <a
                        href="#pricing"
                        className="inline-flex items-center px-6 py-3 bg-white text-mint-600 font-semibold rounded-lg hover:bg-gray-100 transition-colors duration-200"
                      >
                        View Upgrade Options
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* How It Works Section */}
      {!user && (
        <section id="how-it-works" className="py-24 bg-white dark:bg-transparent scroll-mt-24">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <div className="mx-auto max-w-2xl text-center mb-16">
              <h2 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl">
                How It Works
              </h2>
              <p className="mt-4 text-lg text-gray-600 dark:text-gray-300">
                Secure your brand in three simple steps.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-12 relative">
              {/* Connector Line (Desktop) */}
              <div className="hidden md:block absolute top-12 left-1/6 right-1/6 h-0.5 bg-gray-100 dark:bg-dark-700 -z-10" />

              {/* Step 1 */}
              <div className="relative flex flex-col items-center text-center group bg-white dark:bg-dark-800 rounded-2xl p-8 shadow-glow border border-gray-200 dark:border-dark-600">
                <div className="flex items-center justify-center w-24 h-24 rounded-full bg-white dark:bg-dark-800 border-4 border-primary-50 dark:border-primary-900/30 shadow-xl mb-6 group-hover:scale-110 transition-transform duration-300">
                  <MagnifyingGlassIcon className="h-10 w-10 text-primary-500" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">1. Scan Domain</h3>
                <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                  Enter your domain name. Our intelligent engine immediately begins analyzing potential typosquatting variations.
                </p>
              </div>

              {/* Step 2 */}
              <div className="relative flex flex-col items-center text-center group bg-white dark:bg-dark-800 rounded-2xl p-8 shadow-glow border border-gray-200 dark:border-dark-600">
                <div className="flex items-center justify-center w-24 h-24 rounded-full bg-white dark:bg-dark-800 border-4 border-mint-50 dark:border-mint-900/30 shadow-xl mb-6 group-hover:scale-110 transition-transform duration-300">
                  <BoltIcon className="h-10 w-10 text-mint-500" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">2. Detect Vectors</h3>
                <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                  We identify Homoglyphs, Bitsquatting, and context-aware Combosquatting variants that attackers use to deceive your customers.
                </p>
              </div>

              {/* Step 3 */}
              <div className="relative flex flex-col items-center text-center group bg-white dark:bg-dark-800 rounded-2xl p-8 shadow-glow border border-gray-200 dark:border-dark-600">
                <div className="flex items-center justify-center w-24 h-24 rounded-full bg-white dark:bg-dark-800 border-4 border-blue-50 dark:border-blue-900/30 shadow-xl mb-6 group-hover:scale-110 transition-transform duration-300">
                  <ShieldCheckIcon className="h-10 w-10 text-blue-500" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">3. Protect & Remediate</h3>
                <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                  Receive alerts and access our step-by-step <strong><Link to="/login" className="text-primary-600 hover:text-primary-500 underline">Standard Operating Procedure (SOP)</Link></strong> to take down malicious sites instantly.
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Value Propositions */}
      <section id="why-typoguard" className="py-24 sm:py-32 bg-white/50 dark:bg-dark-900 scroll-mt-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-4xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-5xl">
              Why TypoGuard?
            </h2>
            <p className="mt-6 text-lg leading-8 text-gray-600 dark:text-gray-300">
              Detect typosquatting and brand impersonation attempts with our straightforward scanning platform
            </p>
          </div>
          <div className="mx-auto mt-16 grid max-w-5xl grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
            <div className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary-50 to-primary-100 dark:from-primary-900/20 dark:to-primary-800/20 p-8 shadow-glow hover:shadow-glow-lg transition-all duration-300 transform hover:-translate-y-2 border border-primary-200 dark:border-primary-800">
              <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl mb-6 group-hover:scale-110 transition-transform duration-300">
                <EyeIcon className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                Advanced Detection
              </h3>
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                Heuristic-driven detection of domain variants including character substitution, vowel swaps, phonetic attacks, and homoglyphs across 1000+ TLDs.
              </p>
            </div>

            <div className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary-50 to-primary-100 dark:from-primary-900/20 dark:to-primary-800/20 p-8 shadow-glow hover:shadow-glow-lg transition-all duration-300 transform hover:-translate-y-2 border border-primary-200 dark:border-primary-800">
              <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl mb-6 group-hover:scale-110 transition-transform duration-300">
                <BoltIcon className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                Scheduled Monitoring
              </h3>
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                Run scans on-demand or set up automated daily/weekly schedules. Get email notifications when new suspicious domains are discovered.
              </p>
            </div>

            <div className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary-50 to-primary-100 dark:from-primary-900/20 dark:to-primary-800/20 p-8 shadow-glow hover:shadow-glow-lg transition-all duration-300 transform hover:-translate-y-2 border border-primary-200 dark:border-primary-800">
              <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl mb-6 group-hover:scale-110 transition-transform duration-300">
                <ChartBarIcon className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                Professional Reports
              </h3>
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                Clean HTML reports with risk scoring, WHOIS data, and domain analysis. Export findings for compliance and security documentation.
              </p>
            </div>

            <div className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary-50 to-primary-100 dark:from-primary-900/20 dark:to-primary-800/20 p-8 shadow-glow hover:shadow-glow-lg transition-all duration-300 transform hover:-translate-y-2 border border-primary-200 dark:border-primary-800">
              <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl mb-6 group-hover:scale-110 transition-transform duration-300">
                <CogIcon className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                User-Friendly Dashboard
              </h3>
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                Simple web interface for managing domains, reviewing scan results, and configuring alerts. No technical expertise required.
              </p>
            </div>

            <div className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary-50 to-primary-100 dark:from-primary-900/20 dark:to-primary-800/20 p-8 shadow-glow hover:shadow-glow-lg transition-all duration-300 transform hover:-translate-y-2 border border-primary-200 dark:border-primary-800">
              <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl mb-6 group-hover:scale-110 transition-transform duration-300">
                <ShieldCheckIcon className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                Whitelist Management
              </h3>
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                Reduce false positives by whitelisting legitimate domains. Smart tagging system helps classify findings efficiently.
              </p>
            </div>

            <div className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary-50 to-primary-100 dark:from-primary-900/20 dark:to-primary-800/20 p-8 shadow-glow hover:shadow-glow-lg transition-all duration-300 transform hover:-translate-y-2 border border-primary-200 dark:border-primary-800">
              <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl mb-6 group-hover:scale-110 transition-transform duration-300">
                <GlobeAltIcon className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                Enterprise Security
              </h3>
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                Production-ready with security headers, rate limiting, admin controls, and secure authentication via magic links.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Engine Capabilities & Updates */}
      <section id="engine" className="py-24 bg-white dark:bg-transparent border-y border-gray-100 dark:border-dark-800 scroll-mt-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center mb-16">
            <span className="px-3 py-1 text-sm font-bold text-mint-700 bg-mint-100 dark:bg-mint-900/30 rounded-full uppercase tracking-wide">
              🔥 Latest Engine Update: June 2026
            </span>
            <h2 className="mt-6 text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl">
              Advanced Permutation Engine
            </h2>
            <p className="mt-4 text-lg text-gray-600 dark:text-gray-300">
              TypoGuard generates high-fidelity domain variants using sophisticated algorithms tailored to modern cyber threats. Here are the core capabilities of our scanner:
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-gray-50 dark:bg-dark-800 rounded-2xl p-6 border border-gray-200 dark:border-dark-600">
              <span className="text-xs font-bold text-white bg-mint-500 px-2 py-1 rounded mb-4 inline-block">NEW</span>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Bitsquatting</h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm">Detection of hardware and memory bit-flip errors (e.g., <code>google.com</code> &rarr; <code>gopgle.com</code>).</p>
            </div>
            
            <div className="bg-gray-50 dark:bg-dark-800 rounded-2xl p-6 border border-gray-200 dark:border-dark-600">
              <span className="text-xs font-bold text-white bg-mint-500 px-2 py-1 rounded mb-4 inline-block">NEW</span>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Dynamic Combosquatting</h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm">Scrapes the target's homepage to detect its industry and extract exact keywords, appending hyper-targeted terms (e.g., <code>shop-</code>, <code>pay-</code>).</p>
            </div>

            <div className="bg-gray-50 dark:bg-dark-800 rounded-2xl p-6 border border-gray-200 dark:border-dark-600">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2 mt-2">Homoglyphs (IDN)</h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm">Detection of Unicode lookalikes across Latin, Cyrillic, and Greek alphabets (e.g., using a Greek omicron).</p>
            </div>

            <div className="bg-gray-50 dark:bg-dark-800 rounded-2xl p-6 border border-gray-200 dark:border-dark-600">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2 mt-2">Vowel Swapping</h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm">Simulating common phonetic and typing substitution errors made by users.</p>
            </div>

            <div className="bg-gray-50 dark:bg-dark-800 rounded-2xl p-6 border border-gray-200 dark:border-dark-600">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2 mt-2">Smart Subdomain Logic</h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm">Intelligent handling of inputs like <code>backup.example.com</code> to scan the root while generating malicious subdomain variants.</p>
            </div>

            <div className="bg-gray-50 dark:bg-dark-800 rounded-2xl p-6 border border-gray-200 dark:border-dark-600">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2 mt-2">Bilingual Affixing</h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm">Automatically appending high-risk standard terms like <code>login-</code> or <code>support-</code> tailored to standard SLDs.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Product Stream (Demo) */}
      <section id="demo" className="scroll-mt-24">
        <ProductStream />
      </section>

      {/* Visual Attack Analysis (Educational) */}
      <div className="py-24 bg-gray-50 dark:bg-dark-900 border-y border-gray-100 dark:border-dark-800 overflow-hidden">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center mb-16">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl">
              Can You Spot the Fake?
            </h2>
            <p className="mt-4 text-lg text-gray-600 dark:text-gray-300">
              Attackers use subtle visual tricks to fool users. TypoGuard catches what the human eye misses.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
            {/* Example 1: Homoglyph */}
            <div className="bg-white dark:bg-dark-800 rounded-2xl p-8 transform hover:scale-[1.02] transition-transform duration-300 shadow-glow border border-gray-200 dark:border-dark-600">
              <div className="flex items-center justify-between mb-4">
                <span className="px-3 py-1 text-xs font-bold text-red-600 bg-red-100 dark:bg-red-900/30 rounded-full uppercase tracking-wide">
                  Homoglyph Attack
                </span>
                <span className="text-sm text-gray-500">Visual Similarity: 98%</span>
              </div>
              <div className="flex items-center justify-center space-x-4 mb-6">
                <div className="text-center">
                  <span className="block text-sm text-green-600 font-semibold mb-1">Legitimate</span>
                  <code className="bg-gray-100 dark:bg-dark-800 px-3 py-2 rounded-lg text-lg font-mono text-gray-800 dark:text-gray-200">google.com</code>
                </div>
                <span className="text-gray-400 font-bold text-xl">vs</span>
                <div className="text-center">
                  <span className="block text-sm text-red-500 font-semibold mb-1">Malicious</span>
                  <code className="bg-red-50 dark:bg-red-900/10 border border-red-100 dark:border-red-900/30 px-3 py-2 rounded-lg text-lg font-mono text-red-600 dark:text-red-400">googIe.com</code>
                </div>
              </div>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                Attackers replace the letter 'l' (L) with a capital 'I' (i). In many fonts, these look identical. TypoGuard's engine detects these mixed-case anomalies instantly.
              </p>
            </div>

            {/* Example 2: Combustion/Omision */}
            <div className="bg-white dark:bg-dark-800 rounded-2xl p-8 transform hover:scale-[1.02] transition-transform duration-300 shadow-glow border border-gray-200 dark:border-dark-600">
              <div className="flex items-center justify-between mb-4">
                <span className="px-3 py-1 text-xs font-bold text-orange-600 bg-orange-100 dark:bg-orange-900/30 rounded-full uppercase tracking-wide">
                  Typosquatting
                </span>
                <span className="text-sm text-gray-500">Common Misspelling</span>
              </div>
              <div className="flex items-center justify-center space-x-4 mb-6">
                <div className="text-center">
                  <span className="block text-sm text-green-600 font-semibold mb-1">Legitimate</span>
                  <code className="bg-gray-100 dark:bg-dark-800 px-3 py-2 rounded-lg text-lg font-mono text-gray-800 dark:text-gray-200">facebook.com</code>
                </div>
                <span className="text-gray-400 font-bold text-xl">vs</span>
                <div className="text-center">
                  <span className="block text-sm text-red-500 font-semibold mb-1">Malicious</span>
                  <code className="bg-red-50 dark:bg-red-900/10 border border-red-100 dark:border-red-900/30 px-3 py-2 rounded-lg text-lg font-mono text-red-600 dark:text-red-400">facebok.com</code>
                </div>
              </div>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                Missing a single letter is a common typing error. Hackers register these domains to capture traffic meant for your site.
              </p>
          </div>

            {/* Example 3: Bitsquatting */}
            <div className="bg-white dark:bg-dark-800 rounded-2xl p-8 transform hover:scale-[1.02] transition-transform duration-300 shadow-glow border border-gray-200 dark:border-dark-600">
              <div className="flex items-center justify-between mb-4">
                <span className="px-3 py-1 text-xs font-bold text-purple-600 bg-purple-100 dark:bg-purple-900/30 rounded-full uppercase tracking-wide">
                  Bitsquatting
                </span>
                <span className="text-sm text-gray-500">Hardware Bit-Flip</span>
              </div>
              <div className="flex items-center justify-center space-x-4 mb-6">
                <div className="text-center">
                  <span className="block text-sm text-green-600 font-semibold mb-1">Legitimate</span>
                  <code className="bg-gray-100 dark:bg-dark-800 px-3 py-2 rounded-lg text-lg font-mono text-gray-800 dark:text-gray-200">google.com</code>
                </div>
                <span className="text-gray-400 font-bold text-xl">vs</span>
                <div className="text-center">
                  <span className="block text-sm text-red-500 font-semibold mb-1">Malicious</span>
                  <code className="bg-red-50 dark:bg-red-900/10 border border-red-100 dark:border-red-900/30 px-3 py-2 rounded-lg text-lg font-mono text-red-600 dark:text-red-400">gopgle.com</code>
                </div>
              </div>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                A single bit flip in computer memory (RAM) changes 'o' to 'p'. Attackers register these domains to hijack traffic from devices with slight hardware faults.
              </p>
            </div>

            {/* Example 4: Dynamic Combosquatting */}
            <div className="bg-white dark:bg-dark-800 rounded-2xl p-8 transform hover:scale-[1.02] transition-transform duration-300 shadow-glow border border-gray-200 dark:border-dark-600">
              <div className="flex items-center justify-between mb-4">
                <span className="px-3 py-1 text-xs font-bold text-indigo-600 bg-indigo-100 dark:bg-indigo-900/30 rounded-full uppercase tracking-wide">
                  Combosquatting
                </span>
                <span className="text-sm text-gray-500">Social Engineering</span>
              </div>
              <div className="flex items-center justify-center space-x-4 mb-6">
                <div className="text-center">
                  <span className="block text-sm text-green-600 font-semibold mb-1">Legitimate</span>
                  <code className="bg-gray-100 dark:bg-dark-800 px-3 py-2 rounded-lg text-lg font-mono text-gray-800 dark:text-gray-200">paypal.com</code>
                </div>
                <span className="text-gray-400 font-bold text-xl">vs</span>
                <div className="text-center">
                  <span className="block text-sm text-red-500 font-semibold mb-1">Malicious</span>
                  <code className="bg-red-50 dark:bg-red-900/10 border border-red-100 dark:border-red-900/30 px-3 py-2 rounded-lg text-lg font-mono text-red-600 dark:text-red-400">paypal-secure.com</code>
                </div>
              </div>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                By appending context-aware keywords like "secure", "login", or "shop", hackers create highly convincing URLs for phishing campaigns that bypass basic filters.
              </p>
            </div>
          </div>

          <div className="text-center mt-12">
            <a
              href="https://typoguard.io/blog/typosquatting-detection-guide"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center text-primary-600 hover:text-primary-700 font-semibold transition-colors"
            >
              Read our full guide on Typosquatting Mechanics <span className="ml-1">→</span>
            </a>
          </div>
        </div>
      </div>

      {/* Pricing Section */}
      <section id="pricing" className="py-24 sm:py-32 bg-white dark:bg-dark-950 scroll-mt-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center mb-20">
            <h2 className="text-4xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-5xl mb-6">
              Professional Security. Accessible Pricing.
            </h2>
            <p className="text-xl leading-8 text-gray-600 dark:text-gray-300">
              Don't pay enterprise rates for essential protection. Monitor your entire portfolio for the price of a coffee.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 max-w-4xl mx-auto">
            {plans.map((plan) => (
              <div
                key={plan.name}
                className={`relative rounded-3xl p-8 shadow-glow transition-all duration-300 ${plan.popular
                  ? 'bg-gradient-to-br from-primary-500 to-primary-600 text-white ring-2 ring-primary-400 scale-105 z-10 hover:scale-110 hover:shadow-2xl'
                  : 'bg-white dark:bg-dark-800 text-gray-900 dark:text-white border border-gray-200 dark:border-dark-600 hover:shadow-glow-lg hover:scale-105'
                  }`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-20">
                    <span className="bg-orange-500 text-white px-4 py-2 text-sm font-bold rounded-full shadow-lg flex items-center">
                      <span className="mr-1">🔥</span>
                      Best Value
                    </span>
                  </div>
                )}

                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold mb-4">{plan.name}</h3>
                  <div className="flex items-baseline justify-center mb-2">
                    <span className="text-5xl font-bold">{plan.price}</span>
                    {plan.currency && <span className="ml-1 text-2xl font-bold">{plan.currency}</span>}
                    <span className="ml-2 text-lg opacity-75">/{plan.period}</span>
                  </div>
                  {plan.key === 'pro' && (
                    <p className="text-xs font-semibold text-white/90 mb-4 bg-white/20 px-3 py-1 rounded-full inline-block">
                      Competitors charge $120+ for this capacity
                    </p>
                  )}
                  {plan.name === 'Free Plan' && (
                    <p className="text-sm opacity-75">Perfect for getting started</p>
                  )}
                  {plan.name === 'Pro Plan' && (
                    <p className="text-sm opacity-75">For small teams and businesses</p>
                  )}
                  {plan.name === 'Enterprise' && (
                    <p className="text-sm opacity-75">For large organizations</p>
                  )}
                </div>

                <ul className="space-y-4 mb-8">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start">
                      <svg className={`w-5 h-5 mt-0.5 mr-3 flex-shrink-0 ${plan.popular ? 'text-white' : 'text-primary-500'}`} fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className={`text-sm leading-relaxed ${feature.toLowerCase().includes('10 domain') ? 'font-bold' : ''}`}>{feature}</span>
                    </li>
                  ))}
                </ul>

                {plan.name === 'Pro Plan' ? (
                  <button
                    onClick={() => handleUpgrade('pro')}
                    className={`block w-full rounded-xl px-6 py-4 text-center font-bold transition-all duration-200 ${plan.popular
                      ? 'bg-white text-gray-900 hover:bg-gray-50 shadow-lg hover:shadow-xl'
                      : 'bg-gray-900 dark:bg-white text-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-gray-100 shadow-lg hover:shadow-xl'
                      }`}
                  >
                    {
                      user && user.plan_type === 'pro' ? 'Current Plan' : 'Upgrade to Pro'
                    }
                  </button>
                ) : (
                  <Link
                    to="/login"
                    className={`block w-full rounded-xl px-6 py-4 text-center font-bold transition-all duration-200 ${plan.popular
                      ? 'bg-white text-gray-900 hover:bg-gray-50 shadow-lg hover:shadow-xl'
                      : 'bg-gray-900 dark:bg-white text-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-gray-100 shadow-lg hover:shadow-xl'
                      }`}
                  >
                    {plan.cta}
                  </Link>
                )}
              </div>
            ))}
          </div>

          <div className="mt-16 text-center">
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              All plans include domain scanning, email notifications, and HTML reports
            </p>
            <div className="flex items-center justify-center space-x-8 text-sm text-gray-500 dark:text-gray-500">
              <span className="flex items-center">
                <LockClosedIcon className="w-4 h-4 mr-1" />
                Secure Authentication
              </span>
              <span className="flex items-center">
                <ShieldCheckIcon className="w-4 h-4 mr-1" />
                Production Ready
              </span>
              <span className="flex items-center">
                <ExclamationTriangleIcon className="w-4 h-4 mr-1" />
                Easy Setup
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-24 bg-gray-50 dark:bg-dark-900 scroll-mt-24">
        <div className="mx-auto max-w-4xl px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl">
              Frequently Asked Questions
            </h2>
            <p className="mt-4 text-lg text-gray-600 dark:text-gray-300">
              Everything you need to know about protecting your domain.
            </p>
          </div>

          <div className="space-y-6">
            {/* FAQ Item 1 */}
            <details className="group bg-white dark:bg-dark-800 rounded-2xl shadow-glow border border-gray-200 dark:border-dark-600 transition-all duration-200">
              <summary className="flex justify-between items-center cursor-pointer p-6 list-none font-semibold text-lg text-gray-900 dark:text-white">
                <span>How often are domains scanned?</span>
                <span className="transition group-open:rotate-180">
                  <ChevronDownIcon className="h-6 w-6 text-gray-400" />
                </span>
              </summary>
              <div className="text-gray-600 dark:text-gray-400 px-6 pb-6 leading-relaxed">
                <p>
                  Free plans include on-demand scans you can run manually. Pro plans include automated monitoring with daily or weekly frequencies, ensuring you're alerted as soon as a new suspicious domain is registered.
                </p>
              </div>
            </details>

            {/* FAQ Item 2 - Takedown SOP */}
            <details className="group bg-white dark:bg-dark-800 rounded-2xl shadow-glow border border-gray-200 dark:border-dark-600 transition-all duration-200">
              <summary className="flex justify-between items-center cursor-pointer p-6 list-none font-semibold text-lg text-gray-900 dark:text-white">
                <span>Do you help with Takedowns?</span>
                <span className="transition group-open:rotate-180">
                  <ChevronDownIcon className="h-6 w-6 text-gray-400" />
                </span>
              </summary>
              <div className="text-gray-600 dark:text-gray-400 px-6 pb-6 leading-relaxed">
                <p className="mb-3">
                  <strong>Yes.</strong> Finding a malicious site is only half the battle.
                </p>
                <p>
                  Once logged in, TypoGuard provides you with our complete <strong>Takedown SOP (Standard Operating Procedure)</strong>. This comprehensive guide includes legal templates, contact directories for major registrars, and technical steps to report abuse effectively. We empower you to take action and shut down infringing domains.
                </p>
              </div>
            </details>

            {/* FAQ Item 3 */}
            <details className="group bg-white dark:bg-dark-800 rounded-2xl shadow-glow border border-gray-200 dark:border-dark-600 transition-all duration-200">
              <summary className="flex justify-between items-center cursor-pointer p-6 list-none font-semibold text-lg text-gray-900 dark:text-white">
                <span>Do you detect IDN and Punycode attacks?</span>
                <span className="transition group-open:rotate-180">
                  <ChevronDownIcon className="h-6 w-6 text-gray-400" />
                </span>
              </summary>
              <div className="text-gray-600 dark:text-gray-400 px-6 pb-6 leading-relaxed">
                <p>
                  Absolutely. TypoGuard specializes in detecting Homoglyphs (characters that look alike but have different Unicode values) and Punycode variants. These are often used for sophisticated phishing attacks which standard monitoring tools might miss.
                </p>
              </div>
            </details>

            {/* FAQ Item 4 */}
            <details className="group bg-white dark:bg-dark-800 rounded-2xl shadow-glow border border-gray-200 dark:border-dark-600 transition-all duration-200">
              <summary className="flex justify-between items-center cursor-pointer p-6 list-none font-semibold text-lg text-gray-900 dark:text-white">
                <span>What is typosquatting?</span>
                <span className="transition group-open:rotate-180">
                  <ChevronDownIcon className="h-6 w-6 text-gray-400" />
                </span>
              </summary>
              <div className="text-gray-600 dark:text-gray-400 px-6 pb-6 leading-relaxed">
                <p>
                  Typosquatting is a form of cybersquatting where attackers register domains with slight typographical errors of popular brands (like yoursite.com instead of yoursite.com) to intercept traffic and conduct phishing.
                </p>
              </div>
            </details>

            {/* FAQ Item 5 */}
            <details className="group bg-white dark:bg-dark-800 rounded-2xl shadow-glow border border-gray-200 dark:border-dark-600 transition-all duration-200">
              <summary className="flex justify-between items-center cursor-pointer p-6 list-none font-semibold text-lg text-gray-900 dark:text-white">
                <span>Is my data shared with third parties?</span>
                <span className="transition group-open:rotate-180">
                  <ChevronDownIcon className="h-6 w-6 text-gray-400" />
                </span>
              </summary>
              <div className="text-gray-600 dark:text-gray-400 px-6 pb-6 leading-relaxed">
                <p>
                  No. Your monitored domains and scan results are private. We do not sell your data or share your scan history with domain registrars or competitors. Your security operations remain confidential.
                </p>
              </div>
            </details>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 dark:bg-dark-950">
        <div className="mx-auto max-w-7xl px-6 py-12 lg:px-8">
          <div className="text-center">
            <div className="flex items-center justify-center space-x-3 mb-6">
              <span className="text-2xl font-bold text-white">TypoGuard</span>
            </div>
            <p className="text-gray-400 leading-relaxed max-w-2xl mx-auto mb-8">
              Simple domain security monitoring to detect typosquatting attempts.
              Heuristic-driven detection with scheduled scanning and professional reports.
            </p>

            {/* Resources Section */}
            <div className="mb-8">
              <h3 className="text-white font-semibold mb-4">Resources</h3>
              <div className="flex justify-center space-x-6">
                <a
                  href="https://typoguard.io/blog/typosquatting-detection-guide#top"
                  className="text-gray-400 hover:text-white transition-colors duration-200 underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Complete Guide to Typosquatting Detection
                </a>
                <Link
                  to="/glossary"
                  className="text-gray-400 hover:text-white transition-colors duration-200 underline"
                >
                  Cybersecurity Glossary
                </Link>
                <Link
                  to="/use-cases"
                  className="text-gray-400 hover:text-white transition-colors duration-200 underline"
                >
                  Industry Use Cases
                </Link>
              </div>
            </div>

            <div className="border-t border-gray-800 pt-8">
              <p className="text-gray-400 text-sm">
                &copy; 2025 TypoGuard. Domain security monitoring platform.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div >
  );
}
